import React from "react";
import { AlertTriangle, Boxes, RefreshCw, ShieldOff } from "lucide-react";
import DsHero from "../../../../components/DsHero";
import { extractApiErrorMessage } from "../../../../utils/errors";
import { BADGE_ESTADO_PIEZA, BADGE_ESTADO_RECEPCION, ESTADO_PIEZA, ESTADO_RECEPCION, badgeDe, etiqueta } from "./stockFormato";

/**
 * Error de carga de una solapa o un panel: el mensaje del backend y «Reintentar». Se usa en
 * lugar de una tabla vacía, que se confundiría con «no hay nada».
 */
export function ErrorCarga({ error, onReintentar, fallback = "No se pudo cargar la información." }) {
    return (
        <div className="ds-empty-state" role="alert">
            <div className="ds-empty-state__icon"><AlertTriangle size={26} /></div>
            <h3>{extractApiErrorMessage(error, fallback)}</h3>
            {onReintentar && (
                <button type="button" className="ds-btn ds-btn--secondary" onClick={onReintentar} style={{ marginTop: 12 }}>
                    <RefreshCw size={15} />
                    <span>Reintentar</span>
                </button>
            )}
        </div>
    );
}

// Exportado porque la guardia de Técnica (TecnicaRuta.jsx) dice lo mismo, con las mismas palabras.
export const TEXTO_SIN_ACCESO = {
    LEGAJO_DUPLICADO: "Hay más de un legajo activo con tu email. Pedile a RRHH que lo corrija para poder usar el stock.",
    ERROR_ME: "No se pudieron cargar tus permisos. Recargá la página.",
};

/**
 * Pantalla para quien no tiene nivel de stock: no se llama a ninguna API. Con `aviso` dice por
 * qué, cuando no es simplemente que no tiene permiso (useTecnicaStockNivel).
 */
export function SinAccesoStock({ aviso = null }) {
    return (
        <div className="ds-page tec-stock-page">
            <DsHero icon={Boxes} eyebrow="Técnica · Stock" title="Stock" />
            <div className="ds-empty-state">
                <div className="ds-empty-state__icon"><ShieldOff size={26} /></div>
                <h3>{TEXTO_SIN_ACCESO[aviso] || "No tenés acceso al stock de Técnica."}</h3>
            </div>
        </div>
    );
}

/** Celda de KPI del DS v2. Con `onClick` es un filtro que se prende y se apaga (`activo`). */
export function Kpi({ icono: Icono, color, label, value, sub, activo, onClick }) {
    const clickable = typeof onClick === "function";
    return (
        <div
            className={`ds-kpi-cell${clickable ? " ds-kpi-cell--clickable" : ""}${activo ? " ds-kpi-cell--active" : ""}`}
            style={{ "--ds-kpi-accent": color }}
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-pressed={clickable ? !!activo : undefined}
        >
            <div className="ds-kpi-icon" style={{ background: color }}><Icono size={18} /></div>
            <div className="ds-kpi-body">
                <div className="ds-kpi-label">{label}</div>
                <div className="ds-kpi-value">{value ?? "—"}</div>
                {sub && <div className="ds-kpi-sub">{sub}</div>}
            </div>
        </div>
    );
}

/** Badge del estado de recepción de un remito; nada para un ingreso, una devolución o una anulación (NO_APLICA). */
export function EstadoRecepcion({ estado }) {
    const texto = ESTADO_RECEPCION[estado];
    return texto ? <span className={badgeDe(BADGE_ESTADO_RECEPCION, estado)}>{texto}</span> : null;
}

/** Estado de una pieza y, si tiene, el número de su incidente declarado. */
export function EstadoPieza({ pieza }) {
    return (
        <span className="tec-stock-articulo">
            <span className={badgeDe(BADGE_ESTADO_PIEZA, pieza.estado)}>{etiqueta(ESTADO_PIEZA, pieza.estado)}</span>
            {pieza.incidente_declarado && <span className="ds-badge ds-badge--danger">{pieza.incidente_declarado}</span>}
        </span>
    );
}
